import React, { useState, useEffect, useMemo } from "react";
import { Table, message, Row, Col, Card, Space, Select, Input, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import PageBreadcrumb from "../component/PageBreadcrumb";
import { GetCommunityLinks } from "../redux/actions/WorkshopAction";
import AddCommunityLink from "../component/AddCommunityLink";
import dayjs from "dayjs";

const PAGE_SIZE = 10;
const FETCH_LIMIT = 1000;
const CATEGORY_OPTIONS = ["AI", "English Demo"];

const WorkshopCommunityLinks = () => {
  const dispatch = useDispatch();
  const { communityLinks } = useSelector((state) => state.WorkshopReducer);

  const [communityLoader, setCommunityLoader] = useState(false);
  const [communityPage, setCommunityPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState();
  const [urlFilter, setUrlFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    category: undefined,
    url: "",
  });

  useEffect(() => {
    fetchCommunityLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCommunityLinks = () => {
    setCommunityLoader(true);

    const onSuccess = () => {
      setCommunityLoader(false);
    };

    const onError = (error) => {
      setCommunityLoader(false);
      message.error(error, 8);
    };

    dispatch(
      GetCommunityLinks({ page: 1, limit: FETCH_LIMIT }, onSuccess, onError)
    );
  };

  const clearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  const applyFilters = () => {
    setAppliedFilters({
      category: categoryFilter,
      url: (urlFilter || "").trim(),
    });
    setCommunityPage(1);
    clearSelection();
  };

  const resetFilters = () => {
    setCategoryFilter(undefined);
    setUrlFilter("");
    setAppliedFilters({ category: undefined, url: "" });
    setCommunityPage(1);
    clearSelection();
  };

  const handleTableChange = (pagination) => {
    const nextPage =
      typeof pagination === "number" ? pagination : pagination?.current;
    if (!nextPage || nextPage === communityPage) return;
    setCommunityPage(nextPage);
    clearSelection();
  };

  const filteredLinks = useMemo(() => {
    const list = Array.isArray(communityLinks) ? communityLinks : [];
    const category = appliedFilters.category;
    const url = (appliedFilters.url || "").trim().toLowerCase();

    return list.filter((item) => {
      const matchesCategory = category
        ? String(item.category || "").toLowerCase() === String(category).toLowerCase()
        : true;
      const matchesUrl = url
        ? String(item.whatsappLink || "").toLowerCase().includes(url) ||
          String(item.route || "").toLowerCase().includes(url)
        : true;
      return matchesCategory && matchesUrl;
    });
  }, [communityLinks, appliedFilters]);

  const communityColumns = [
    {
      title: "No",
      width: 60,
      key: "no",
      render: (_, __, index) => (communityPage - 1) * PAGE_SIZE + index + 1,
    },
    { title: "Category", width: 140, dataIndex: "category", key: "category" },
    { title: "Route", width: 120, dataIndex: "route", key: "route" },
    {
      title: "WhatsApp Link",
      dataIndex: "whatsappLink",
      key: "whatsappLink",
      ellipsis: true,
      render: (link) =>
        link ? (
          <a href={link} target="_blank" rel="noopener noreferrer">
            {link}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Workshop Date",
      width: 180,
      dataIndex: "workshopDate",
      key: "workshopDate",
    },
    {
      title: "Workshop Time",
      width: 140,
      dataIndex: "workshopTime",
      key: "workshopTime",
    },
    {
      title: "Updated At",
      width: 120,
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Action",
      key: "edit",
      width: 80,
      fixed: "right",
      render: (record) => (
        <AddCommunityLink
          value={record}
          onSaved={fetchCommunityLinks}
        />
      ),
    },
  ];

  return (
    <div>
      <PageBreadcrumb route={["Community Links"]} />

      <div style={{ marginTop: 20 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 16,
                  alignItems: "center",
                }}
              >
                <h3 style={{ margin: 0 }}>Workshop Community Links</h3>
                <Space>
                  <AddCommunityLink
                    isBulk
                    name="Bulk Edit"
                    selectedRows={selectedRows}
                    onSaved={() => {
                      clearSelection();
                      fetchCommunityLinks();
                    }}
                  />
                  <AddCommunityLink
                    name="Add Community Link"
                    onSaved={() => {
                      clearSelection();
                      fetchCommunityLinks();
                    }}
                  />
                </Space>
              </div>

              <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
                <Col xs={24} sm={8} md={6}>
                  <Select
                    allowClear
                    showSearch
                    placeholder="Filter by category"
                    style={{ width: "100%" }}
                    value={categoryFilter}
                    onChange={(value) => setCategoryFilter(value)}
                    options={CATEGORY_OPTIONS.map((category) => ({
                      label: category,
                      value: category,
                    }))}
                  />
                </Col>
                <Col xs={24} sm={10} md={8}>
                  <Input
                    allowClear
                    placeholder="Filter by URL"
                    value={urlFilter}
                    onChange={(e) => setUrlFilter(e.target.value)}
                    onPressEnter={applyFilters}
                  />
                </Col>
                <Col>
                  <Space>
                    <Button type="primary" onClick={applyFilters}>
                      Search
                    </Button>
                    <Button onClick={resetFilters}>Reset</Button>
                  </Space>
                </Col>
              </Row>

              <Table
                columns={communityColumns}
                dataSource={filteredLinks}
                rowKey={(record) =>
                  record._id || `${record.category}-${record.route}`
                }
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys, rows) => {
                    setSelectedRowKeys(keys);
                    setSelectedRows(rows);
                  },
                }}
                scroll={{ x: 1100 }}
                loading={communityLoader}
                pagination={{
                  current: communityPage,
                  total: filteredLinks.length,
                  pageSize: PAGE_SIZE,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    total
                      ? `${range[0]}-${range[1]} of ${total} items`
                      : "0 items",
                }}
                onChange={handleTableChange}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default WorkshopCommunityLinks;
